import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * PageHeader — the single shared page/section header for BOTH the staff app
 * and the external portals: an optional breadcrumb, an optional gold eyebrow,
 * the title, an optional subtitle/description, and a primary-action slot.
 * Gives every screen the same top structure so the surfaces read as one
 * product. (The former `@/components/layout/PageHeader` now re-exports this.)
 */
export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  /** Gold uppercase label above the title (e.g. "Broker portal"). */
  eyebrow?: string;
  /** Muted supporting line under the title. `description` is an alias. */
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Right-aligned primary action(s) (e.g. a Button). */
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  eyebrow,
  subtitle,
  description,
  breadcrumbs,
  actions,
  className,
}: PageHeaderProps) {
  const support = description ?? subtitle;
  return (
    <header className={cn("mb-space-5 flex flex-col gap-space-2", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-space-1 list-none p-0 m-0 text-caption text-gray-mid">
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <li key={idx} className="flex items-center gap-space-1">
                  {crumb.to && !isLast ? (
                    <Link
                      to={crumb.to}
                      className="text-gray-mid hover:text-onyx underline-offset-2 hover:underline rounded-sm focus-visible:outline-none focus-visible:shadow-goldRing"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span aria-current={isLast ? "page" : undefined} className={isLast ? "text-onyx" : undefined}>
                      {crumb.label}
                    </span>
                  )}
                  {!isLast && <ChevronRight size={12} aria-hidden className="text-gray-mid" />}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      <div className="flex flex-col gap-space-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div className="mb-space-2 font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
              {eyebrow}
            </div>
          )}
          {/* `break-words` is load-bearing. `min-w-0` above lets this column
              shrink below its content (a flex item's default `min-width: auto`
              would otherwise refuse to), so without a wrapping rule here a long
              unbroken title paints OUTSIDE its box. `break-words` — NOT
              `truncate`, which implies nowrap and would silently clip the
              wrapping titles ~151 consumers rely on — keeps the glyphs inside.

              `flex-1` is the other half: the identity column now GROWS into the
              free space, so the actions cluster is sized by its content instead
              of splitting the row evenly. Before that, a header with actions
              gave the title roughly half the row and shattered a long name one
              syllable per line. */}
          <h1 className="m-0 font-display text-h2 font-extrabold uppercase tracking-wider text-onyx break-words">
            {title}
          </h1>
          {support && <p className="mt-space-2 m-0 max-w-reading text-body text-gray-mid">{support}</p>}
        </div>
        {/* NOT `shrink-0`. That kept the cluster at its full natural width no
            matter how narrow the row got, which is what starved the identity
            column above — `min-w-0` there meant the title, not the buttons,
            absorbed every pixel of the squeeze. The cluster may now shrink AND
            wrap onto a second line; `justify-end` keeps it right-aligned when
            it does, and `min-w-0` lets a wide action (a long button label, a
            search field) participate in the shrink instead of forcing overflow. */}
        {actions && (
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-space-3">{actions}</div>
        )}
      </div>
    </header>
  );
}
